import React, {useEffect, useRef, useState} from 'react';
import {notification} from 'antd';
import {Button, ButtonTypes} from '../../ui-kit/Button';
import Input from '../../ui-kit/Input';
import {CopyButton} from '../TicketGroup/ShareModal';
import {CopyButtons, ModalButtons, ModalSubtitle, ModalTitle, ModalWrapper} from '../TicketGroup/styles';
import {generateOrderPdf, getOrderPdfDownloadUrl, lookupOrderPdf} from '../../queries/ticketGroup';
import {OrderPdfSummaryT} from '../../queries/types';
import {OrderPdfDialog, OrderPdfInput, OrderPdfSummary} from './styles';

const POLL_INTERVAL_MS = 10 * 1000;
const POLL_LIMIT_MS = 15 * 60 * 1000;
const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

type OrderPdfFileT = {fileName?: string; fileUrl?: string};

const showError = (error: any, fallback: string) =>
  notification.error({message: 'Error', description: error?.response?.body?.message || fallback});

export const OrderPdfModal: React.FC<{onClose: () => void}> = ({onClose}) => {
  const [input, setInput] = useState('');
  const [order, setOrder] = useState<OrderPdfSummaryT>();
  const [single, setSingle] = useState<OrderPdfFileT>();
  const [individual, setIndividual] = useState<OrderPdfFileT>();
  const [checking, setChecking] = useState(false);
  const [generating, setGenerating] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const loadFiles = async (ticketureOrderId: string) => {
    const [singleRes, individualRes] = await Promise.all([
      getOrderPdfDownloadUrl({ticketureOrderId, individual: false}),
      getOrderPdfDownloadUrl({ticketureOrderId, individual: true}),
    ]);
    if (!mounted.current) return;
    setSingle(singleRes?.body);
    setIndividual(individualRes?.body);
  };

  // Large orders can outlast the browser's connection while the server keeps
  // generating, so follow the saved status instead of reporting a failure.
  const waitForGeneration = async (ticketureOrderId: string) => {
    const startedAt = Date.now();
    while (mounted.current && Date.now() - startedAt < POLL_LIMIT_MS) {
      const res = await lookupOrderPdf(ticketureOrderId);
      if (res?.body?.pdfStatus !== 'inProgress') return res?.body as OrderPdfSummaryT;
      await wait(POLL_INTERVAL_MS);
    }
  };

  const followGeneration = async (ticketureOrderId: string) => {
    const result = await waitForGeneration(ticketureOrderId);
    if (!mounted.current) return;
    if (result) setOrder(result);
    if (result?.pdfStatus === 'completed') return loadFiles(ticketureOrderId);
    notification.error({message: 'Error', description: 'The PDFs could not be generated. Please try again.'});
  };

  const onCheck = async () => {
    if (!input.trim()) return;
    try {
      setChecking(true);
      setOrder(undefined);
      setSingle(undefined);
      setIndividual(undefined);
      const res = await lookupOrderPdf(input.trim());
      const found: OrderPdfSummaryT = res?.body;
      if (!mounted.current) return;
      setOrder(found);
      if (found?.pdfStatus === 'completed') await loadFiles(found.ticketureOrderId);
      if (found?.pdfStatus === 'inProgress') {
        setGenerating(true);
        await followGeneration(found.ticketureOrderId);
      }
    } catch (error: any) {
      showError(error, 'We could not find that order.');
    } finally {
      if (mounted.current) {
        setChecking(false);
        setGenerating(false);
      }
    }
  };

  const onGenerate = async () => {
    if (!order) return;
    const {ticketureOrderId} = order;
    try {
      setGenerating(true);
      setSingle(undefined);
      setIndividual(undefined);
      try {
        const res = await generateOrderPdf(ticketureOrderId);
        if (!mounted.current) return;
        setOrder(res?.body);
        await loadFiles(ticketureOrderId);
      } catch (error: any) {
        if (error?.status && error.status < 500) throw error;
        await followGeneration(ticketureOrderId);
      }
    } catch (error: any) {
      showError(error, 'The PDFs could not be generated. Please try again.');
    } finally {
      if (mounted.current) setGenerating(false);
    }
  };

  const isGenerated = order?.pdfStatus === 'completed';
  return (
    <OrderPdfDialog visible onCancel={onClose} centered>
      <ModalWrapper>
        <ModalTitle>Generate PDF tickets from an order</ModalTitle>
        <ModalSubtitle>
          Paste an order URL, ticket URL or order number. No ticket group is needed, and the PDFs use the standard
          ticket format.
        </ModalSubtitle>
        <OrderPdfInput>
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onPressEnter={onCheck}
            placeholder="Order URL, ticket URL or order number"
            disabled={generating}
          />
          <Button variant={ButtonTypes.tertiary} loading={checking} disabled={generating} onClick={onCheck}>
            Check
          </Button>
        </OrderPdfInput>
        {order && (
          <OrderPdfSummary>
            <b>{order.ticketureOrderNumber}</b> | {order.customerName} | {order.venueName} | {order.ticketCount} tickets
            {order.ticketGroup && (
              <div>
                This order belongs to the group for {order.ticketGroup.clientName}, so its PDFs keep that client&apos;s
                name and logo.
              </div>
            )}
          </OrderPdfSummary>
        )}
        {generating && <ModalSubtitle>Generating PDF tickets. This can take a few minutes.</ModalSubtitle>}
        {order && (
          <ModalButtons>
            <Button variant={ButtonTypes.tertiary} loading={generating} onClick={onGenerate}>
              {isGenerated ? 'Regenerate PDFs' : 'Generate PDFs'}
            </Button>
            {single?.fileUrl && !generating && (
              <Button variant={ButtonTypes.primary} href={single.fileUrl} download={single.fileName}>
                Single PDF
              </Button>
            )}
            {individual?.fileUrl && !generating && (
              <Button variant={ButtonTypes.primary} href={individual.fileUrl} download={individual.fileName}>
                Individual PDFs
              </Button>
            )}
          </ModalButtons>
        )}
        {!generating && (
          <CopyButtons>
            <CopyButton title={'Single PDF Link'} url={single?.fileUrl} />
            <CopyButton title={'Zip File Link'} url={individual?.fileUrl} />
          </CopyButtons>
        )}
      </ModalWrapper>
    </OrderPdfDialog>
  );
};
